import * as Sentry from "@sentry/react";

import { logError } from "../../utils/error";
import { invokeClaudeStream } from "./apiClient";
import { buildAdaptiveThinkingParams } from "./modelUtils";
import { QueryResult, ThinkingModeEffortLevel } from "./types";

const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 10000;
const RETRY_BACKOFF_FACTOR = 2;
const RETRYABLE_HTTP_STATUSES = [502, 503, 504, 429];
const SSE_DATA_PREFIX = "data: ";
const SSE_DATA_PREFIX_LENGTH = SSE_DATA_PREFIX.length;
const SSE_DONE_MESSAGE = "[DONE]";
const SSE_FINAL_RESPONSE_TYPE = "final_response";
const SSE_ERROR_TYPE = "error";
const STREAM_ERROR_MESSAGE = "Chunk stream failed";

interface ChunkStreamFrame {
  type?: string;
  data?: unknown;
  usage?: unknown;
  error?: { message?: string };
}

const parseSseFrame = (line: string): ChunkStreamFrame | null => {
  if (!line.startsWith(SSE_DATA_PREFIX)) {
    return null;
  }

  const data = line.slice(SSE_DATA_PREFIX_LENGTH);
  if (data === SSE_DONE_MESSAGE) {
    return null;
  }

  try {
    return JSON.parse(data);
  } catch (_parseError) {
    logError("Failed to parse SSE data", _parseError);
    return null;
  }
};

const extractFinalResponse = (line: string): { data?: any; usage: any } | null => {
  const frame = parseSseFrame(line);

  if (frame?.type === SSE_ERROR_TYPE) {
    throw new Error(frame.error?.message || STREAM_ERROR_MESSAGE);
  }

  if (frame?.type !== SSE_FINAL_RESPONSE_TYPE) {
    return null;
  }

  return {
    data: frame.data,
    usage: frame.usage || {}
  };
};

const processChunkStreamResponse = async (response: Response): Promise<{ data?: any; usage: any }> => {
  const reader = response.body?.getReader();
  const decoder = new TextDecoder();

  if (!reader) {
    throw new Error("No response body available");
  }

  let completeText = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      completeText += decoder.decode(value, { stream: true });
    }

    const lines = completeText.split("\n");
    for (let i = lines.length - 1; i >= 0; i--) {
      const result = extractFinalResponse(lines[i]);
      if (result) {
        return result;
      }
    }

    console.warn("No final_response found in stream");
    return { data: undefined, usage: {} };

  } finally {
    reader.releaseLock();
  }
};

export interface ChunkQueryParams {
  query: string;
  context: unknown[];
  temperature?: number;
  maxTokens?: number;
  budgetTokens?: number;
  effort?: ThinkingModeEffortLevel;
  model?: string;
  thinkingMode?: boolean;
  jsonSchema?: any;
  extractionName?: string;
}

const RETRY_CONFIG = {
  maxRetries: MAX_RETRIES,
  initialDelay: INITIAL_RETRY_DELAY_MS,
  maxDelay: MAX_RETRY_DELAY_MS,
  backoffFactor: RETRY_BACKOFF_FACTOR,
  retryableStatuses: RETRYABLE_HTTP_STATUSES
};

const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

const isRetryableError = (response: Response | null, error: unknown): boolean => {
  if (response && RETRY_CONFIG.retryableStatuses.includes(response.status)) {
    return true;
  }

  if (error instanceof TypeError && error.message.includes("fetch")) {
    return true;
  }

  return false;
};

const withRetry = async <T>(
  fn: () => Promise<T>,
  options: {
    maxRetries?: number;
    initialDelay?: number;
    maxDelay?: number;
    backoffFactor?: number;
    shouldRetry?: (error: unknown, attempt: number) => boolean;
    onRetry?: (error: unknown, attempt: number) => void;
  } = {}
): Promise<T> => {
  const {
    maxRetries = RETRY_CONFIG.maxRetries,
    initialDelay = RETRY_CONFIG.initialDelay,
    maxDelay = RETRY_CONFIG.maxDelay,
    backoffFactor = RETRY_CONFIG.backoffFactor,
    shouldRetry = () => true,
    onRetry = () => {}
  } = options;

  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      if (attempt < maxRetries && shouldRetry(error, attempt)) {
        const delayMs = Math.min(
          initialDelay * Math.pow(backoffFactor, attempt),
          maxDelay
        );

        onRetry(error, attempt);
        await delay(delayMs);
      } else {
        throw error;
      }
    }
  }

  throw lastError;
};

const queryChunkWithRetry = async (
  params: ChunkQueryParams
): Promise<Response> => {
  return withRetry(
    async () => {
      const response = await invokeClaudeStream("claude-chunk-query", {
        query: params.query,
        context: params.context,
        temperature: params.temperature,
        maxTokens: params.maxTokens,
        ...buildAdaptiveThinkingParams({ model: params.model ?? "", effort: params.effort, budgetTokens: params.budgetTokens }),
        model: params.model,
        thinkingMode: params.thinkingMode,
        jsonSchema: params.jsonSchema,
        extractionName: params.extractionName
      });

      if (!response.ok && isRetryableError(response, null)) {
        throw new Error(`Request failed with retryable status ${response.status}`);
      }

      return response;
    },
    {
      shouldRetry: (error, _attempt) => {
        if (error instanceof TypeError && error.message.includes("fetch")) {
          return true;
        }
        if (error instanceof Error && error.message.includes("retryable status")) {
          return true;
        }
        return false;
      },
      onRetry: (error, attempt) => {
        const message = error instanceof Error ? error.message : String(error);
        console.warn(
          `Request failed: ${message}. Retrying... (attempt ${attempt + 1}/${RETRY_CONFIG.maxRetries})`
        );
      }
    }
  );
};

export const queryChunkWithClaude = async (
  params: ChunkQueryParams
): Promise<QueryResult> => {
  try {
    const response = await queryChunkWithRetry(params);

    if (!response.ok) {
      let errorMessage = "Failed to query chunk with Claude";
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch {
        errorMessage = `${errorMessage} (Status: ${response.status})`;
      }

      return {
        ok: false,
        error: new Error(errorMessage)
      };
    }

    const result = await processChunkStreamResponse(response);
    return {
      ok: true,
      response: "",
      thinking: undefined,
      usage: result.usage,
      data: result.data
    };
  } catch (error) {
    logError("Error querying chunk with Claude", error);
    const isAbortError = error instanceof DOMException && error.name === "AbortError";

    if (!isAbortError) {
      Sentry.captureException(error, {
        tags: {
          function: "queryChunkWithClaude",
          module: "claude-chunk-query"
        },
        extra: {
          hasQuery: !!params.query,
          contextLength: params.context?.length || 0
        }
      });
    }

    return {
      ok: false,
      error: isAbortError
        ? new Error("Request was canceled")
        : (error instanceof Error ? error : new Error("An unknown error occurred"))
    };
  }
};
